import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { Translate, List, X, Phone } from "@phosphor-icons/react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/contact";
import { resolveSeo, SEO_LANGS, BASE_LANGS } from "@/data/seoEngine";

const LANG_NAMES = { de: "Deutsch", en: "English", fr: "Français", it: "Italiano", es: "Español", pt: "Português", ko: "한국어", zh: "中文", ja: "日本語", ar: "العربية", ckb: "کوردی سۆرانی", kmr: "Kurdî", fa: "فارسی", he: "עברית", el: "Ελληνικά", hi: "हिन्दी" };

export default function Navbar() {
  const { t, lang, setLang } = useI18n();
  const loc = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

  const overHero = isHome && !scrolled && !menuOpen;

  const segs = loc.pathname.split("/").filter(Boolean);
  const urlLang = SEO_LANGS.includes(segs[0]) ? segs[0] : null;
  const currentLang = urlLang || lang;

  const switchLang = (target) => {
    if (!target || target === currentLang) return;
    setLang(target);
    // If currently on a programmatic-SEO landing page, jump to its translated URL variant.
    let bare = null;
    if (segs.length >= 2 && SEO_LANGS.includes(segs[0])) bare = segs.slice(1).join("/");
    else if (segs.length === 1 && !SEO_LANGS.includes(segs[0])) bare = segs[0];
    if (bare && resolveSeo(bare)) navigate(target === "de" ? `/${bare}` : `/${target}/${bare}`);
  };

  const LangSelect = ({ className }) => (
    <div className={`relative inline-flex items-center ${className || ""}`} data-testid="lang-switcher-wrap">
      <Translate size={16} className="absolute left-2.5 pointer-events-none" />
      <select value={currentLang} onChange={(e) => switchLang(e.target.value)} data-testid="lang-switcher"
        aria-label="Language" className="appearance-none bg-transparent border border-current/20 rounded-full pl-8 pr-6 py-2 text-sm font-body cursor-pointer focus:outline-none">
        {SEO_LANGS.map((l) => (<option key={l} value={l} className="text-[#0B1533]">{LANG_NAMES[l]}</option>))}
      </select>
    </div>
  );

  const goSection = (id) => {
    setMenuOpen(false);
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#" + id);
    }
  };

  const links = [
    { label: t("nav_book"), to: "/", key: "book" },
    { label: t("nav_destinations"), to: "/destinations", key: "destinations" },
    { label: t("nav_routes"), to: "/strecken", key: "routes" },
    { label: t("nav_prices"), to: "/preise", key: "prices" },
    { label: t("nav_attractions"), to: "/ausfluege", key: "attractions" },
    { label: t("nav_events"), to: "/events", key: "events" },
    { label: t("nav_blog"), to: "/blog", key: "blog" },
    { label: t("nav_faq"), to: "/faq", key: "faq" },
    { label: t("nav_contact"), to: "/contact", key: "contact" },
  ];

  const txt = overHero ? "text-white" : "text-[#0B1533]";
  const sub = overHero ? "text-white/75" : "text-[#5A607A]";

  const LinkEl = ({ l }) =>
    l.to ? (
      <Link to={l.to} data-testid={`nav-${l.key}`} className={`font-body text-sm transition-colors duration-200 ${loc.pathname === l.to ? txt + " font-semibold" : sub}`}>
        {l.label}
      </Link>
    ) : (
      <button onClick={() => goSection(l.section)} data-testid={`nav-${l.key}`} className={`font-body text-sm transition-colors duration-200 ${sub}`}>
        {l.label}
      </button>
    );

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${overHero ? "bg-transparent" : "bg-white/90 backdrop-blur-xl border-b border-[#E9E5DE]"}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-link">
          <span className={`inline-flex items-center rounded-xl transition-all duration-300 ${overHero ? "bg-white px-3 py-1.5" : ""}`}>
            <img src="/airport-transfer-24-logo.png" alt="Airport Transfer Zurich – Flughafentransfer & Taxi Zürich" className="h-9 sm:h-10 w-auto" />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l, i) => <LinkEl key={i} l={l} />)}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${PHONE_TEL}`} data-testid="nav-call" className={`hidden md:inline-flex items-center gap-2 text-sm font-body font-semibold rounded-full px-5 py-2.5 transition-all duration-200 ${overHero ? "bg-white text-[#0B1533] hover:bg-white/90" : "bg-[#EF5A1E] text-white hover:bg-[#D2450F]"}`}>
            <Phone size={16} weight="fill" /> {PHONE_DISPLAY}
          </a>
          <LangSelect className={`inline-flex ${txt}`} />
          <button onClick={() => setMenuOpen((v) => !v)} data-testid="menu-toggle" aria-label="Menu" className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full ${overHero ? "text-white" : "text-[#0B1533]"}`}>
            {menuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-[#E9E5DE] px-4 sm:px-6 py-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {links.map((l, i) => (
              <div key={i} className="text-[#0B1533]">
                {l.to ? (
                  <Link to={l.to} data-testid={`mnav-${l.key}`} className="font-heading font-semibold text-xl tracking-tight">{l.label}</Link>
                ) : (
                  <button onClick={() => goSection(l.section)} data-testid={`mnav-${l.key}`} className="font-heading font-semibold text-xl tracking-tight">{l.label}</button>
                )}
              </div>
            ))}
            <Link to="/manage" className="font-body text-sm text-[#5A607A] mt-2">{t("nav_manage")}</Link>
            <Link to="/admin/login" className="font-body text-sm text-[#5A607A]">{t("nav_admin")}</Link>
            <div className="flex items-center gap-3 pt-4 border-t border-[#E9E5DE] mt-2">
              <a href={`tel:${PHONE_TEL}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full py-3">
                <Phone size={16} weight="fill" /> {t("call_now")}
              </a>
              <LangSelect className="text-[#0B1533]" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
